import React, { memo, useEffect, useMemo, useState } from 'react';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { isEqual } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { useSelector } from 'react-redux';
import AdvancedSearch from '../../common/components/AdvancedSearch/AdvancedSearch';
import { isMobile } from '../../common/services/isMobile';
import ChargerDetailsModal from '../ChargerDetails/components/ChargerDetailsModal';
import { getChargersAdvancedSearchConfig } from '../ChargersList/constants';
import DashboardChargersList from './components/DashboardChargersList';
import GMap from './components/GMap';
import LoadIndicator from './components/LoadIndicator';
import ViewModeSelect from './components/ViewModeSelect';
import { getDashBoardFlag } from '../../common/services/AuthenticationRules';
import { getAdvancedSearchExcludedFields } from '../ChargersList/components/ChargersListToolbar';
import { getUser } from '../Login/selectors';

export const dashboardTableId = 'dashboard';

export const dashboardDefaultSorting = { id: 'c_uuid', desc: false };

const ChargerModalWrap = ({ closeChangerModal }) => {
  const { chargerId, tab } = useParams();

  return <ChargerDetailsModal closeChangerModal={closeChangerModal} isMobile={isMobile} chargerId={chargerId} tab={tab} />;
};

export const MAP_MODE_LIST = 'LIST';
export const MAP_MODE_REGULAR = 'REGULAR';

const Dashboard = memo(
  ({
    stopAutoUpdate,
    setAutoscale,
    viewMode,
    setViewMode,
    getChargers,
    filterQuery,
    setDashboardAdvancedSearchFilter,
    updateList,
    chargers,
    config,
    setFilterFinished,
  }) => {
    const [prevViewMode, setPrevViewMode] = useState(viewMode);
    const navigate = useNavigate();

    const monitorChargers = () => getChargers(filterQuery, uuidv4());

    const user = useSelector(getUser);

    const excludedFields = useMemo(() => getAdvancedSearchExcludedFields(config, user), [config, user]);
    const isListMode = useMemo(() => viewMode === MAP_MODE_LIST, [viewMode]);

    const openChangerModal = (data) => {
      const path = data ? `/dashboard/details/${data.id}` : '/dashboard';

      navigate(path, { replace: true });
    };

    const handleViewMode = (mode) => {
      const path = mode === MAP_MODE_LIST ? '/dashboard/list-view' : '/dashboard';

      navigate(path, { replace: true });
      setViewMode(mode);
    };

    useEffect(() => {
      if (!getDashBoardFlag(config)) {
        navigate('/chargers/chargers_list');
      }
    }, [config]);

    useEffect(() => {
      if (window.location.pathname && window.location.pathname.includes('/dashboard/list-view') && !isListMode) {
        handleViewMode(MAP_MODE_LIST);
      }

      if (window.location.pathname && !window.location.pathname.includes('/dashboard/list-view') && isListMode) {
        handleViewMode(MAP_MODE_REGULAR);
      }

      if (!isListMode) monitorChargers();

      setAutoscale();

      return () => {
        stopAutoUpdate();
      };
    }, []);

    useEffect(() => {
      if (viewMode !== prevViewMode) {
        !isListMode ? monitorChargers() : stopAutoUpdate();
      }

      setPrevViewMode(viewMode);
    }, [viewMode]);

    const onFilterApply = (query) => {
      setDashboardAdvancedSearchFilter(query);

      if (isListMode) {
        updateList(query);
      } else {
        getChargers(query, uuidv4());
        setAutoscale();
      }
    };

    const FilterControl = () => (
      <>
        <AdvancedSearch
          isShowAdvancedSearch
          filterButtonLabel="Filter"
          id={dashboardTableId}
          excludedFields={excludedFields}
          getFields={getChargersAdvancedSearchConfig}
          onApply={onFilterApply}
          onToggle={() => {}}
        />
        <ViewModeSelect isMobile={isMobile} onChange={setViewMode} viewMode={viewMode} />
      </>
    );

    return (
      <div className={`i2p2-map-block ${viewMode === MAP_MODE_LIST ? 'list-mode' : 'map-mode'}`}>
        {isListMode ? (
          <>
            <div className="map-controls__filter">
              <FilterControl />
            </div>
            <div className="chargers-list-container">
              <DashboardChargersList openChangerModal={false} advancedSearchEnabled />
            </div>
          </>
        ) : (
          <>
            <FilterControl />
            <div className="map-controls__load-indicator">
              <LoadIndicator />
            </div>
          </>
        )}
        <GMap
          openChangerModal={openChangerModal}
          setFilterFinished={setFilterFinished}
          viewMode={viewMode}
          chargers={chargers}
          config={config}
        />
        <Routes>
          <Route exact path="/details/:chargerId/:tab?/:dataType?" element={<ChargerModalWrap closeChangerModal={openChangerModal} />} />
        </Routes>
      </div>
    );
  },
  (prevProps, nextProps) =>
    isEqual(prevProps.viewMode, nextProps.viewMode) &&
    isEqual(prevProps.filterQuery, nextProps.filterQuery) &&
    isEqual(prevProps.chargers, nextProps.chargers) &&
    isEqual(prevProps.config, nextProps.config),
);

export default Dashboard;
